// import cloneDeep from 'lodash.clonedeep'
import i18next from 'i18next'
import { CallType } from '@weblite/ts-action/media-proxy'
import { OngoingCall } from '@weblite/ts-action/room'
import { CallMessageStatus } from '@weblite/ts-action/message'
import { showEndOngoingCallConfirmModal } from '$comp/modals/confirmation-dialog'
import { CallStorage, callStorage } from './storage'
import { getPv } from '$logic/pv'
import { getHall } from '$logic/hall'
import { getRoom } from '$logic/room'
import { CallWappPanel } from '$logic/wapp-panels'
import { getClientUserId } from '$logic/client-user'
import { getUser, getUserDisplayName } from '$logic/users'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { HallCallType } from '$comp/modals/create-hall-call/choose-call-time'
import { formatToLocaleDateTime } from '$helper/functions/date'
import {
  getUserToken,
  getTextToken,
} from '$comp/_app/components/chat/components/body/components/messageList/components/event/react-hook.helper'
import { MINUTE_MS } from '$helper/functions/time'
// import { showCallNPS } from '$logic/call/service'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

const SECOND_MS = 1000
export function isCallStarted(roomCall?: OngoingCall) {
  const now = dateWithOffset()
  return Boolean(
    roomCall &&
      roomCall.startedAt &&
      roomCall.startedAt - Number(now) < SECOND_MS,
  )
}

export function formatCallDuration(duration?: number) {
  if (!duration) return ''

  const inSeconds = Math.round(duration / 1000)
  const hours = Math.floor(inSeconds / 3600)
  const minutes = Math.floor((inSeconds % 3600) / 60)
  const seconds = Math.floor(inSeconds % 60)

  return [hours, minutes, seconds]
    .filter((val, index) => (index === 0 ? Boolean(val) : true))
    .map(val =>
      val.toLocaleString(i18next.language, {
        minimumIntegerDigits: 2,
        useGrouping: false,
      }),
    )
    .join(' : ')
}

export function formatScheduledCallEvent(
  startingAt: Date,
  callType: HallCallType,
  userName?: string,
) {
  const callTypeTranslated = t(`call.${callType}`)
  const dateTranslated = formatToLocaleDateTime(startingAt)
  return t('call.scheduledCallEventMessage')(
    callTypeTranslated,
    dateTranslated,
    userName && `${userName} `,
  )
}

export function getCallScheduledEventTokens(
  startingAt: Date,
  callType: HallCallType,
  userId: string,
) {
  const callTypeTranslated = t(`call.${callType}`)
  const dateTranslated = formatToLocaleDateTime(startingAt)
  return [
    getUserToken(userId),
    getTextToken(
      t('call.scheduledCallEventMessage')(
        callTypeTranslated,
        dateTranslated,
        '',
      ),
    ),
  ]
}

export function formatCallDurationVerbally(duration = 0) {
  const inSeconds = Math.round(duration / 1000)
  if (inSeconds < 60) return `${toLocale(inSeconds)} ${t('common.second')}`

  const hours = Math.floor(inSeconds / 3600)
  const minutes = Math.floor((inSeconds % 3600) / 60)
  return [hours, minutes]
    .map((value, index) =>
      value
        ? `${toLocale(value)} ${t(
            index === 0 ? 'common.hour' : 'common.minute',
          )}`
        : '',
    )
    .filter(Boolean)
    .join(t('common.and'))
}

export function getCallMessageStatusText(
  status: CallMessageStatus,
  senderId: string,
) {
  const fromMe = getClientUserId() === senderId

  return t(
    `call.status.${
      status === 'timed-out' || status === 'successful'
        ? fromMe
          ? 'me'
          : 'opponent'
        : 'common'
    }.${status}`,
  )
}

async function getCallTitle(callType: CallType, roomId: string) {
  if (callType === 'call') {
    const { to } = await getPv(roomId)
    const user = await getUser(to)
    return getUserDisplayName(user)
  }

  // callType: group_call | live | tribune
  const {
    roomId: ongoingCallRoomId,
    hallMeta: { name: ongoingCallName },
  } = callStorage.get()

  if (roomId === ongoingCallRoomId && ongoingCallName) return ongoingCallName

  const { call } = (await getRoom(roomId)) || {}
  if (call?.name) return call.name

  const { name } = await getHall(roomId)
  return name
}

export async function endOngoingCallWithConfirmation(
  newCallRoomId: string,
  newCallType: CallType,
) {
  const {
    roomId: ongoingCallRoomId,
    callType: ongoingCallType,
    pvMeta: { state: pvCallState },
  } = callStorage.get()

  if (!ongoingCallRoomId || !ongoingCallType) return

  if (
    newCallRoomId === ongoingCallRoomId ||
    (ongoingCallType === 'call' && pvCallState === 'incoming')
  )
    return

  callStorage.set({ hasNextCall: true })
  const ongoingCallTitle = await getCallTitle(
    ongoingCallType,
    ongoingCallRoomId,
  )
  const newCallTitle = await getCallTitle(newCallType, newCallRoomId)

  await showEndOngoingCallConfirmModal({
    ongoingCallType,
    ongoingCallTitle,
    newCallType,
    newCallTitle,
    onConfirm: () => CallWappPanel.closeCall(true, false),
  })
}

export function isCallAtLeastOneMinuteLong(
  call: Pick<CallStorage, 'joinedAt'>,
) {
  if (!call.joinedAt) return false
  return new Date().getTime() - call.joinedAt > MINUTE_MS
}

export function getCallId(
  call: Pick<CallStorage, 'callType' | 'roomId' | 'startedAt'>,
) {
  return call.callType && call.roomId && call.startedAt
    ? `${call.callType}${call.roomId}${call.startedAt}`
    : ''
}

export async function resetCallAndShowNPS(
  hideCallPanel?: boolean,
  sendEndCallEventToWApp?: boolean,
) {
  // const call = cloneDeep(callStorage.get()) as CallStorage
  callStorage.reset()

  await CallWappPanel.closeCall(hideCallPanel, sendEndCallEventToWApp)
  // showCallNPS(call)
}

export function getDefaultCallTitle(hallName: string, callType: HallCallType) {
  return `${hallName} - ${t(`call.${callType}`)}`
}
